import { useId } from "react";

interface P {
  value: number;
  min?: number;
  max: number;
  step?: number;
  onChange: (v: number) => void;
  width?: number | string;
  disabled?: boolean;
  ariaLabel?: string;
}

const THUMB = 12; // px

/**
 * Custom range slider where the filled portion of the track lines up exactly
 * with the thumb centre. Native `<input type=range>` positions the thumb at
 * `(value/max) * (trackWidth - thumbWidth) + thumbWidth/2`, so a plain linear
 * gradient at N% would drift by up to `thumbWidth/2` pixels. We compensate.
 */
export function Slider({
  value, min = 0, max, step = 0.01, onChange, width = "100%", disabled, ariaLabel,
}: P) {
  const id = useId().replace(/[^a-zA-Z0-9]/g, "");
  const pct = max > min ? ((value - min) / (max - min)) * 100 : 0;
  const offset = THUMB / 2 - (pct * THUMB) / 100; // px, positive at low pct, negative at high
  const stop = `calc(${pct}% + ${offset}px)`;
  return (
    <>
      <input
        className={`slider-${id}`}
        type="range"
        min={min}
        max={max}
        step={step}
        value={Math.min(Math.max(value, min), max)}
        onChange={(e) => onChange(Number(e.target.value))}
        disabled={disabled}
        aria-label={ariaLabel}
        style={{ width, display: "block" }}
      />
      <style>{`
        .slider-${id} {
          -webkit-appearance: none;
          appearance: none;
          height: 4px;
          background: linear-gradient(to right, var(--accent) 0, var(--accent) ${stop}, var(--line) ${stop}, var(--line) 100%);
          border-radius: 3px;
          outline: none;
          cursor: ${disabled ? "not-allowed" : "pointer"};
          opacity: ${disabled ? 0.5 : 1};
        }
        .slider-${id}::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: ${THUMB}px;
          height: ${THUMB}px;
          border-radius: 50%;
          background: var(--accent);
          border: none;
          cursor: ${disabled ? "not-allowed" : "grab"};
          box-shadow: 0 1px 3px rgba(0,0,0,.4);
        }
        .slider-${id}::-webkit-slider-thumb:active { cursor: grabbing; }
        .slider-${id}::-moz-range-thumb {
          width: ${THUMB}px;
          height: ${THUMB}px;
          border-radius: 50%;
          background: var(--accent);
          border: none;
          cursor: ${disabled ? "not-allowed" : "grab"};
          box-shadow: 0 1px 3px rgba(0,0,0,.4);
        }
        .slider-${id}::-moz-range-track {
          height: 4px;
          background: linear-gradient(to right, var(--accent) 0, var(--accent) ${stop}, var(--line) ${stop}, var(--line) 100%);
          border-radius: 3px;
        }
      `}</style>
    </>
  );
}
